import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trackAPIError } from '../../shared/utils/sentryTracking';
import { logger } from '../../shared/utils/logger';

export interface UseWorkflowBiometricConsentReturn {
  isLoading: boolean;
  error: string | null;
  consentText: string | null;
  definitionUuid: string | null;
  fetchConsent: (biometricConsentApiBaseUrl: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Biometric consent hook for the Workflow Engine flow.
 *
 * Fetches consent definition via the IPA BFF proxy using session cookie
 * credentials — no session token needed since the BFF injects it server-side.
 * Separate from useBiometricConsent (IVA flow) to avoid coupling.
 */
export function useWorkflowBiometricConsent(): UseWorkflowBiometricConsentReturn {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentText, setConsentText] = useState<string | null>(null);
  const [definitionUuid, setDefinitionUuid] = useState<string | null>(null);

  const fetchConsent = useCallback(async (biometricConsentApiBaseUrl: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const language = i18n.language || 'en';
      const url = `${biometricConsentApiBaseUrl}/v1/consents/definitions?consentHandle=biometric_consent&language=${language}`;

      logger.log('[WorkflowBiometricConsent] Fetching consent definition via BFF:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        // cross-origin (websdk.idme.cc → enroll.idme.cc) — must include credentials
        // so the IPA session cookie is forwarded to the BFF for token injection.
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        const apiMessage = errorBody?.errors?.[0]?.message || errorBody?.detail || null;
        const err = new Error(apiMessage ?? `Failed to fetch consent: ${response.status}`);
        (err as any).apiMessage = apiMessage;
        (err as any).httpStatus = response.status;
        throw err;
      }

      const data = await response.json();
      const definition = data?.data?.[0];

      if (!definition) throw new Error('No consent definition found');

      setConsentText(definition.consentText);
      setDefinitionUuid(definition.definitionUuid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      trackAPIError(err, 'GET /v1/consents/definitions');
      setError(message);
      setConsentText(null);
      setDefinitionUuid(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  const clearError = useCallback(() => setError(null), []);

  return { isLoading, error, consentText, definitionUuid, fetchConsent, clearError };
}
